import { Quat } from '@/core/math/Quat';
import { Vec3 } from '@/core/math/Vec3';
import { clamp } from '@/core/math/scalar';
import {
  FreeFlightSpeedState,
  applyWheel,
  createSpeedState,
  resolveSpeed,
  settleOverride,
} from './FreeFlightSpeed';

/**
 * Free-flight camera: a debug tool, and deliberately not a spacecraft.
 *
 * Everything else in this project refuses to assign position or velocity
 * directly, because doing so is how a flight model stops meaning anything. This
 * is the exception, and it is safe *because* it is an exception: free flight
 * detaches the camera from the vessel entirely. The vessel keeps flying its own
 * physics, untouched, while the camera moves under kinematic control. Nothing
 * the camera does can be mistaken for something the simulation did.
 *
 * It exists because inspecting a planet-scale world any other way means
 * spending real minutes in transit, and because several of the worst bugs in
 * this codebase were only visible from somewhere a vessel could not easily
 * reach.
 *
 * Speed is continuous and logarithmic. The useful range spans twelve orders of
 * magnitude — a centimetre per second to inspect a landing leg, ten billion
 * metres per second to cross the system while you watch — so the control has to
 * be on the exponent. A linear speed slider over that range is unusable, and
 * discrete stops are always either too coarse where you are working or too fine
 * where you are travelling.
 */
export class FreeFlightCamera {
  /** True while the camera is detached from the vessel. */
  active = false;

  /** Position in the active body's inertial frame, metres. */
  readonly position = new Vec3();
  readonly orientation = Quat.identity();

  /**
   * Cruise setting and any local override, as log10(m/s). All the rules live in
   * FreeFlightSpeed.ts, which is pure and tested on its own.
   */
  private speedState: FreeFlightSpeedState = createSpeedState(2);

  /**
   * The pilot's cruise setting, log10(m/s). Persisted; see DevSessionState.
   * Writing it clears any override, because setting a speed outright is not
   * the same gesture as pushing through a ceiling.
   */
  get speedExponent(): number {
    return this.speedState.cruiseExponent;
  }

  set speedExponent(value: number) {
    this.speedState = createSpeedState(clamp(value, FreeFlightCamera.MIN_EXPONENT, FreeFlightCamera.MAX_EXPONENT));
  }

  /** Rotation rate at full stick, rad/s. */
  turnRate = 1.6;

  /** Slowest and fastest the camera may be flown, as log10(m/s). */
  static readonly MIN_EXPONENT = -2;
  static readonly MAX_EXPONENT = 10;

  /** Decades of speed per mouse wheel notch. */
  wheelSensitivity = 0.12;

  /**
   * Ceiling imposed by whatever the camera is near, metres per second.
   * `Infinity` in open space. Set per frame by `Game`; see
   * `proximitySpeedLimit`.
   *
   * Deliberately separate from `speedExponent` rather than clamping it. The
   * exponent is the pilot's own setting and is never written by the limiter, so
   * flying away from a body restores exactly the speed that was set before
   * arriving at it — no wheeling back up, and no memory of the limit once it
   * stops applying.
   */
  speedLimit = Infinity;

  /** The cruise speed the pilot set, ignoring proximity and any override. */
  get speed(): number {
    return Math.pow(10, this.speedState.cruiseExponent);
  }

  /** What the camera will actually fly at. */
  get effectiveSpeed(): number {
    return resolveSpeed(this.speedState, this.speedLimit).speed;
  }

  /** True while the camera is flying slower than the cruise setting asks. */
  get speedLimited(): boolean {
    return resolveSpeed(this.speedState, this.speedLimit).limited;
  }

  /** True while the pilot is deliberately holding above the proximity ceiling. */
  get speedOverridden(): boolean {
    return this.speedState.overrideExponent !== null;
  }

  /**
   * Speed ceiling for a camera at `altitude` metres above the surface.
   *
   * Keyed to time-to-impact rather than to distance: the cap is whatever speed
   * would take `APPROACH_SECONDS` to close the remaining altitude, so the pilot
   * always has the same reaction time whether they are a kilometre above a moon
   * or a thousand kilometres above a gas giant. A cap keyed to absolute
   * distance instead would be wrong on every body but the one it was tuned
   * against.
   *
   * Started at 3 seconds, which flew too slowly to be pleasant near a surface;
   * halving it doubles the ceiling at every altitude. Both constants scale the
   * same curve, so this is the one knob worth reaching for.
   *
   * The floor keeps the camera movable when it is sitting on the ground, where
   * the formula alone would converge to zero and freeze it.
   *
   * Scales itself out of the way: at interplanetary range the ceiling is
   * enormous, so the limiter is inert exactly where the twelve-decade speed
   * range is the point.
   */
  static readonly APPROACH_SECONDS = 1.5;
  static readonly MIN_LIMIT = 10;

  static proximitySpeedLimit(altitude: number): number {
    if (!Number.isFinite(altitude)) return Infinity;
    return Math.max(FreeFlightCamera.MIN_LIMIT, altitude / FreeFlightCamera.APPROACH_SECONDS);
  }

  /**
   * Shifts the speed by a number of decades. Whether that moves the cruise
   * setting or an override depends on the proximity ceiling — see
   * FreeFlightSpeed.ts.
   */
  adjustSpeed(decades: number): void {
    this.speedState = applyWheel(this.speedState, decades, this.speedLimit, {
      min: FreeFlightCamera.MIN_EXPONENT,
      max: FreeFlightCamera.MAX_EXPONENT,
    });
  }

  faster(): void {
    this.adjustSpeed(0.5);
  }

  slower(): void {
    this.adjustSpeed(-0.5);
  }

  /** Detaches the camera at a given state. */
  engage(position: Vec3, orientation: Quat): void {
    this.position.copy(position);
    this.orientation.copy(orientation);
    this.active = true;
  }

  disengage(): void {
    this.active = false;
  }

  /**
   * Advances the camera.
   *
   * @param rotation body-axis stick, same convention as a vessel
   * @param translation body-axis movement demand, -1..1
   * @param boost multiplier applied while the boost key is held
   * @param wheelNotches mouse wheel travel this frame, in notches
   */
  update(rotation: Vec3, translation: Vec3, boost: boolean, wheelNotches: number, dt: number): void {
    if (!this.active) return;

    // Drop an override the ceiling has stopped binding (the departure case)
    // before reading the wheel, so a notch on the way out moves cruise rather
    // than a spent override.
    this.speedState = settleOverride(this.speedState, this.speedLimit);

    if (wheelNotches !== 0) this.adjustSpeed(wheelNotches * this.wheelSensitivity);

    if (rotation.lengthSq() > 0) {
      // The stick is a body-axis rate demand, exactly as it is for a vessel, so
      // the camera responds to the same inputs in the same directions. It is
      // rotated into the parent frame before integrating.
      const omegaBody = rotation.clone().clampLength(1).mulScalar(this.turnRate);
      this.orientation.integrate(this.orientation.rotate(omegaBody), dt);
    }

    if (translation.lengthSq() > 0) {
      // Boost multiplies the LIMITED speed rather than escaping the limit, so
      // it stays a deliberate override the pilot is holding down: an 8x escape
      // hatch when the ceiling is genuinely in the way, not a way to arrive at
      // a surface at interplanetary speed by leaning on one key.
      const scale = this.effectiveSpeed * (boost ? 8 : 1) * dt;
      const move = this.orientation.rotate(translation.clone().clampLength(1));
      this.position.addScaled(move, scale);
    }
  }

  /**
   * Moves the camera to a body without any pretence of having travelled there.
   *
   * @param bodyPosition body centre in the camera's current frame
   * @param standoff distance to sit off the surface, metres
   */
  jumpTo(bodyPosition: Vec3, radius: number, standoff = 2.5): void {
    const away = Vec3.subVectors(this.position, bodyPosition);
    const direction = away.lengthSq() > 1e-6 ? away.normalize() : new Vec3(0, 0, 1);
    this.position.copy(bodyPosition).addScaled(direction, radius * standoff);
    // Look back at what was jumped to.
    const forward = Vec3.subVectors(bodyPosition, this.position).normalize();
    this.orientation.copy(Quat.lookRotation(forward, direction));
  }
}
